/* FZ: Discount popup v1 (2026-10-05) - assets/fz-discount-popup.js
   <fz-discount-popup> custom element, rendered by sections/fz-discount-popup.liquid.
   localStorage 'fz_dp_state': (none) = never seen, 'min' = closed -> tab shown, 'done' = signed up.
   Vanilla JS, no dependencies. Loaded with defer. */
(() => {
  if (customElements.get('fz-discount-popup')) return;
  window.FZ = window.FZ || {};
  window.FZ.discountPopup = true;

  const STATE_KEY = 'fz_dp_state';
  const FLAG_KEY = 'fz_dp_submitted';
  const EDITOR_KEY = 'fz_dp_editor_open';
  const RETRY_MS = 4000;

  const read = (area, key) => {
    try { return window[area].getItem(key); } catch (e) { return null; }
  };
  const write = (area, key, value) => {
    try { window[area].setItem(key, value); return true; } catch (e) { return false; }
  };
  const remove = (area, key) => {
    try { window[area].removeItem(key); } catch (e) { /* storage blocked */ }
  };
  const storageWorks = (() => {
    const ok = write('localStorage', 'fz_dp_test', '1');
    remove('localStorage', 'fz_dp_test');
    return ok;
  })();

  class FzDiscountPopup extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.tab = this.querySelector('.fz-dp-tab');
      this.form = this.querySelector('form');
      if (!this.dialog || !this.tab) return;

      this.timers = [];
      this.submitting = false;
      this.designMode = this.hasAttribute('data-design-mode');
      this.done = !this.designMode && read('localStorage', STATE_KEY) === 'done';

      this.handleTabClick = () => this.open();
      this.handleDialogClick = (event) => {
        const target = event.target;
        if (target === this.dialog || (target.closest && target.closest('[data-fz-dp-close]'))) this.minimize();
      };
      this.handleCancel = (event) => {
        event.preventDefault();
        this.minimize();
      };
      this.handleClose = () => document.documentElement.classList.remove('fz-dp-lock');
      this.handleSubmit = (event) => {
        // Only fires once the browser's own email validation has passed.
        if (this.submitting) {
          event.preventDefault();
          return;
        }
        this.submitting = true;
        write('sessionStorage', FLAG_KEY, '1');
        const button = this.form.querySelector('[type="submit"]');
        if (button) button.setAttribute('aria-disabled', 'true');
      };
      this.handlePageShow = (event) => {
        if (event.persisted) this.resetSubmit();
      };

      this.tab.addEventListener('click', this.handleTabClick);
      this.dialog.addEventListener('click', this.handleDialogClick);
      this.dialog.addEventListener('cancel', this.handleCancel);
      this.dialog.addEventListener('close', this.handleClose);
      if (this.form) this.form.addEventListener('submit', this.handleSubmit);
      window.addEventListener('pageshow', this.handlePageShow);

      if (this.designMode) {
        this.initEditor();
        return;
      }

      const result = this.readPostResult();
      if (result === 'success') {
        this.showSuccess();
        return;
      }
      if (result === 'error') {
        this.open();
        return;
      }

      if (this.done) return;
      const state = read('localStorage', STATE_KEY);
      if (state === 'min' || !storageWorks) {
        this.showTab();
        return;
      }
      if (this.dataset.autoOpen === 'true') this.schedule(this.seconds('delay', 8) * 1000);
    }

    disconnectedCallback() {
      if (this.timers) this.clearTimers();
      document.documentElement.classList.remove('fz-dp-lock');
      if (this.handlePageShow) window.removeEventListener('pageshow', this.handlePageShow);
      if (this.handleSelect) {
        document.removeEventListener('shopify:section:select', this.handleSelect);
        document.removeEventListener('shopify:section:deselect', this.handleDeselect);
      }
    }

    /* After the native form post, Shopify reloads the page with ?customer_posted=true.
       Dawn's footer newsletter can post too, so only react if the post was ours. */
    readPostResult() {
      const posted = this.querySelector('[data-fz-dp-posted]');
      const errored = this.querySelector('[data-fz-dp-error]');
      if (!posted && !errored) return null;

      const ours =
        window.location.hash === `#${this.dataset.formId}` || read('sessionStorage', FLAG_KEY) === '1';
      remove('sessionStorage', FLAG_KEY);
      if (!ours) return null;

      try {
        const url = new URL(window.location.href);
        url.searchParams.delete('customer_posted');
        if (url.hash === `#${this.dataset.formId}`) url.hash = '';
        window.history.replaceState(window.history.state, '', url.toString());
      } catch (e) { /* leave URL as is */ }

      return posted ? 'success' : 'error';
    }

    schedule(ms) {
      this.later(() => {
        const state = read('localStorage', STATE_KEY);
        if (state === 'done') return;
        if (state === 'min') {
          this.showTab();
          return;
        }
        if (this.isBusy()) {
          this.schedule(RETRY_MS);
          return;
        }
        this.open();
      }, ms);
    }

    // Don't interrupt: cart drawer, menu drawer, search or quick-add open, typing, another dialog, hidden tab.
    isBusy() {
      if (document.visibilityState === 'hidden') return true;
      if (/(^|\s)overflow-hidden/.test(document.body.className)) return true;
      if (document.querySelector('dialog[open]')) return true;
      const active = document.activeElement;
      return !!(active && active.matches && active.matches('input, textarea, select, [contenteditable="true"]'));
    }

    open() {
      if (this.dialog.hasAttribute('open')) return;
      this.clearTimers();
      this.hideTab();
      if (typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }
      document.documentElement.classList.add('fz-dp-lock');
    }

    close() {
      if (this.dialog.hasAttribute('open')) {
        if (typeof this.dialog.close === 'function') {
          this.dialog.close();
        } else {
          this.dialog.removeAttribute('open');
        }
      }
      document.documentElement.classList.remove('fz-dp-lock');
    }

    minimize() {
      this.close();
      if (this.done) return;
      if (!this.designMode) write('localStorage', STATE_KEY, 'min');
      this.showTab();
    }

    showSuccess(preview) {
      if (!preview) {
        this.done = true;
        write('localStorage', STATE_KEY, 'done');
        this.hideTab();
      }
      this.setView('success');
      this.open();
      const heading = this.querySelector('[data-fz-dp-success-heading]');
      if (heading) heading.focus();
      if (preview) return;
      this.applyDiscount();
      this.later(() => this.close(), this.seconds('closeAfter', 5) * 1000);
    }

    /* Shopify's shareable discount link sets the code on the cart / checkout.
       redirect=cart.js keeps the response tiny instead of downloading the home page. */
    applyDiscount() {
      const code = (this.dataset.code || '').trim();
      if (!code || !window.fetch) return;
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const url = `${root}discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(`${root}cart.js`)}`;
      fetch(url, { credentials: 'same-origin' })
        .then(() => {
          // Dawn PubSub: lets the cart drawer / cart page re-render with the discount.
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'fz-discount-popup' });
          }
        })
        .catch(() => { /* the code is still shown in the message as a backup */ });
    }

    initEditor() {
      const id = this.dataset.sectionId;
      this.handleSelect = (event) => {
        if (!event.detail || event.detail.sectionId !== id) return;
        write('sessionStorage', EDITOR_KEY, '1');
        this.openPreview();
      };
      this.handleDeselect = (event) => {
        if (!event.detail || event.detail.sectionId !== id) return;
        remove('sessionStorage', EDITOR_KEY);
        this.close();
        this.showTab();
      };
      document.addEventListener('shopify:section:select', this.handleSelect);
      document.addEventListener('shopify:section:deselect', this.handleDeselect);

      this.showTab();
      // The editor re-renders the section on every setting change; keep it open while editing.
      if (read('sessionStorage', EDITOR_KEY) === '1') this.openPreview();
    }

    openPreview() {
      if (this.hasAttribute('data-preview-success')) {
        this.showSuccess(true);
      } else {
        this.setView('form');
        this.open();
      }
    }

    setView(name) {
      this.querySelectorAll('[data-fz-dp-view]').forEach((view) => {
        view.hidden = view.getAttribute('data-fz-dp-view') !== name;
      });
    }

    showTab() {
      this.tab.hidden = false;
    }

    hideTab() {
      this.tab.hidden = true;
    }

    resetSubmit() {
      this.submitting = false;
      const button = this.form && this.form.querySelector('[type="submit"]');
      if (button) button.removeAttribute('aria-disabled');
    }

    seconds(name, fallback) {
      const value = parseInt(this.dataset[name], 10);
      return Number.isFinite(value) && value >= 0 ? value : fallback;
    }

    later(fn, ms) {
      this.timers.push(window.setTimeout(fn, ms));
    }

    clearTimers() {
      this.timers.forEach((timer) => window.clearTimeout(timer));
      this.timers = [];
    }
  }

  customElements.define('fz-discount-popup', FzDiscountPopup);
})();